import React, { useEffect, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error TS2322 raw box-shadow string not in ADS shadow token union
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transform: 'scale(1)',
		transformOrigin: 'right center',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',
	},
	reveal: {
		// @ts-expect-error TS2353 combined descendant selectors are supported at runtime
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- descendant reveal driven by parent hover/focus state
		'&:hover [data-expandable-pill], &:focus-within [data-expandable-pill]': {
			gridTemplateColumns: '1fr',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- descendant reveal driven by parent hover/focus state
		'&:hover [data-expandable-pill-clip], &:focus-within [data-expandable-pill-clip]': {
			transform: 'translateX(0)',
		},
	},
	hoverScale: {
		'&:hover': {
			transform: 'scale(1.125)',
		},
		'&:active': {
			transform: 'scale(1.125)',
		},
	},
	expandPill: {
		display: 'grid',
		gridTemplateColumns: '0fr',
		overflow: 'hidden',
		transition: 'grid-template-columns 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	expandPillClip: {
		whiteSpace: 'nowrap',
		minWidth: 0,
		transform: 'translateX(100%)',
		willChange: 'transform',
		transition: 'transform 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
});

type RovoButtonExpanderProps = {
	children: React.ReactNode;
	expandedContent?: React.ReactNode;
	isExpansionSuppressed?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	testId?: string;
};

export function RovoButtonExpander({
	children,
	expandedContent,
	isExpansionSuppressed = false,
	onExpandedChange,
	testId,
}: RovoButtonExpanderProps): React.JSX.Element {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocusWithin, setIsFocusWithin] = useState(false);
	const isExpanded = !isExpansionSuppressed && (isHovered || isFocusWithin);

	useEffect(() => {
		onExpandedChange?.(isExpanded);
	}, [isExpanded, onExpandedChange]);

	return (
		<Box
			testId={testId}
			xcss={cx(styles.pill, !isExpansionSuppressed && styles.reveal, styles.hoverScale)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsFocusWithin(true)}
			onBlur={(event: React.FocusEvent<HTMLElement>) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node)) {
					setIsFocusWithin(false);
				}
			}}
		>
			<Box xcss={styles.expandPill} data-expandable-pill>
				<Box xcss={styles.expandPillClip} data-expandable-pill-clip>
					{expandedContent}
				</Box>
			</Box>
			{children}
		</Box>
	);
}
